import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layout } from "views/common/Layout";
import { Card } from "views/components/ui/Card";
import AdminSidebar from "views/components/admin/AdminSidebar";
import Pagination from "views/components/ui/Pagination";
import {
    fetchAdminTools,
    updateToolStatus,
    updateToolInfo,
    submitTool,
    deleteTool,
    fetchToolDetails,
    submitBatchTools,
} from "actions/tool";
import { submitBatchMcps } from "actions/mcp";
import { fetchCategories } from "actions/category";
import ToolSubmitForm from "views/components/ToolSubmitForm";
import StatsOverview from "views/components/admin/StatsOverview";
import ToolTable from "views/components/admin/ToolTable";
import ToolDetailsModal from "views/components/admin/ToolDetailsModal";
import BlogPostEditor from "views/components/admin/BlogPostEditor";
import {
    fetchBlogTitles,
    updateBlogTitle,
    deleteBlogTitle,
    addBlogTitle,
    processAllPendingBlogTitles,
} from "actions/admin";
import BlogTitlesTable from "views/components/admin/BlogTitlesTable";
import QueueManagement from "views/components/admin/QueueManagement";
import BatchToolSubmit from "views/components/admin/BatchToolSubmit";
import BatchMcpSubmit from "views/components/admin/BatchMcpSubmit";
import URLCrawler from "views/components/admin/URLCrawler";
import { crawlURL } from "actions/admin";
import CategoryManagement from "views/components/admin/CategoryManagement";

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const {
        adminTools,
        adminToolsPagination,
        adminToolsLoading,
        adminToolsError,
    } = useSelector((state) => state.tool);
    const { categories } = useSelector((state) => state.category);
    const { blogTitles, isLoading: blogTitlesLoading } = useSelector(
        (state) => state.admin
    );
    const [selectedTool, setSelectedTool] = useState(null);
    const [showToolDetails, setShowToolDetails] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState("");
    const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [activeTab, setActiveTab] = useState("all");

    useEffect(() => {
        dispatch(fetchAdminTools(currentPage, currentStatus));
        dispatch(fetchCategories());
        if (activeTab === "blog-titles") {
            dispatch(fetchBlogTitles());
        }
    }, [dispatch, currentPage, currentStatus, activeTab]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        switch (tab) {
            case "all":
                setCurrentStatus(null);
                break;
            case "pending":
                setCurrentStatus("Under Review");
                break;
            case "approved":
                setCurrentStatus("Approved");
                break;
            case "rejected":
                setCurrentStatus("Rejected");
                break;
            case "error":
                setCurrentStatus("Processing Error");
                break;
            default:
                setCurrentStatus(null);
        }
        setCurrentPage(1); // Reset to first page when changing tabs
    };

    // Get counts for stats from the pagination data
    const stats = {
        pending: adminToolsPagination.totalByStatus?.pending || 0,
        approved: adminToolsPagination.totalByStatus?.approved || 0,
        rejected: adminToolsPagination.totalByStatus?.rejected || 0,
        error: adminToolsPagination.totalByStatus?.error || 0,
    };

    const handleApproveTool = (toolUuid) => {
        dispatch(updateToolStatus(toolUuid, "Approved"));
    };

    const handleRejectTool = (toolUuid) => {
        dispatch(updateToolStatus(toolUuid, "Rejected"));
    };

    const handleViewTool = async (tool) => {
        try {
            // First set the tool to show loading state
            setSelectedTool(tool);
            setShowToolDetails(true);
            setIsEditing(false);

            // Fetch fresh tool details
            const updatedTool = await dispatch(fetchToolDetails(tool.uuid));
            if (updatedTool) {
                setSelectedTool(updatedTool);
                // Set initial category and subcategory from the fresh data
                setSelectedCategoryId(
                    updatedTool.tool_category?.category || ""
                );
                setSelectedSubCategoryId(
                    updatedTool.tool_category?.subCategory || ""
                );
            }
        } catch (error) {
            console.error("Error fetching tool details:", error);
            // Keep the original tool data if fetch fails
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleSaveEdit = async (editedTool) => {
        try {
            await dispatch(
                updateToolInfo(editedTool.uuid, editedTool, {
                    categoryId: selectedCategoryId,
                    subCategoryId: selectedSubCategoryId,
                })
            );
            // Refetch the tool details after successful update
            const updatedTool = await dispatch(
                fetchToolDetails(editedTool.uuid)
            );
            if (updatedTool) {
                setSelectedTool(updatedTool);
            }
            setIsEditing(false);
        } catch (error) {
            console.error("Error updating tool:", error);
            // Keep the modal open and editing state if there's an error
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
    };

    const handleSubmitTool = (toolData) => {
        const toolWithCategory = {
            ...toolData,
            categoryId: selectedCategoryId,
            subCategoryId: selectedSubCategoryId,
        };
        dispatch(submitTool(toolWithCategory));
    };

    const handleCategoryChange = (categoryId) => {
        setSelectedCategoryId(categoryId);
        setSelectedSubCategoryId(""); // Reset subcategory when category changes
    };

    const handleSubCategoryChange = (subCategoryId) => {
        setSelectedSubCategoryId(subCategoryId);
    };

    const handleDeleteTool = (toolUuid) => {
        if (
            window.confirm(
                "Are you sure you want to delete this tool? This action cannot be undone."
            )
        ) {
            dispatch(deleteTool(toolUuid));
        }
    };

    const handleUpdateBlogTitleStatus = (titleId, status) => {
        dispatch(updateBlogTitle(titleId, status));
    };

    const handleDeleteBlogTitle = (titleId) => {
        if (
            window.confirm(
                "Are you sure you want to delete this blog title? This action cannot be undone."
            )
        ) {
            dispatch(deleteBlogTitle(titleId));
        }
    };

    const handleAddBlogTitle = (formData) => {
        dispatch(addBlogTitle(formData));
    };

    const handleProcessAllBlogTitles = () => {
        return dispatch(processAllPendingBlogTitles());
    };

    const handleBatchSubmit = async (tools) => {
        try {
            await dispatch(submitBatchTools(tools));
        } catch (error) {
            // Error is already handled in the action
            console.error("Batch submit error:", error);
        }
    };

    const handleBatchMcpSubmit = async (mcps) => {
        try {
            await dispatch(submitBatchMcps(mcps));
            // Refresh the tools list after successful submission
            dispatch(fetchAdminTools(currentPage, currentStatus));
        } catch (error) {
            console.error("Batch MCP submit error:", error);
            throw error;
        }
    };

    const handleCrawlURL = async (url) => {
        try {
            const tools = await dispatch(crawlURL(url));
            return tools;
        } catch (error) {
            console.error("Error crawling URL:", error);
            throw error;
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case "all":
            case "pending":
            case "approved":
            case "rejected":
            case "error":
                return (
                    <Card>
                        <div className="p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                {activeTab === "all" && "All Tools"}
                                {activeTab === "pending" &&
                                    "Tools Pending Review"}
                                {activeTab === "approved" && "Approved Tools"}
                                {activeTab === "rejected" && "Rejected Tools"}
                                {activeTab === "error" &&
                                    "Tools with Processing Error"}
                            </h2>
                            {adminToolsError && (
                                <div className="text-red-600 mb-4">
                                    {adminToolsError}
                                </div>
                            )}
                            <ToolTable
                                tools={adminTools}
                                categories={categories}
                                onViewTool={handleViewTool}
                                onApproveTool={handleApproveTool}
                                onRejectTool={handleRejectTool}
                                onDeleteTool={handleDeleteTool}
                                showDeleteButton={
                                    activeTab === "approved" ||
                                    activeTab === "error" ||
                                    activeTab === "rejected"
                                }
                                isLoading={adminToolsLoading}
                            />
                            {adminToolsPagination.totalPages > 1 && (
                                <Pagination
                                    currentPage={adminToolsPagination.page}
                                    totalPages={adminToolsPagination.totalPages}
                                    onPageChange={handlePageChange}
                                />
                            )}
                        </div>
                    </Card>
                );
            case "queue":
                return <QueueManagement />;
            case "submit":
                return (
                    <Card>
                        <ToolSubmitForm
                            onSubmit={handleSubmitTool}
                            onCancel={() => {}}
                        />
                    </Card>
                );
            case "batch-submit":
                return <BatchToolSubmit onSubmit={handleBatchSubmit} />;
            case "batch-mcp-submit":
                return <BatchMcpSubmit onSubmit={handleBatchMcpSubmit} />;
            case "blog":
                return (
                    <Card>
                        <div className="p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                Blog Posts Editor
                            </h2>
                            <BlogPostEditor />
                        </div>
                    </Card>
                );
            case "blog-titles":
                return (
                    <Card>
                        <div className="p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                Blog Titles Management
                            </h2>
                            <BlogTitlesTable
                                blogTitles={blogTitles}
                                isLoading={blogTitlesLoading}
                                onDelete={handleDeleteBlogTitle}
                                onAdd={handleAddBlogTitle}
                                onProcessAll={handleProcessAllBlogTitles}
                            />
                        </div>
                    </Card>
                );
            case "url-crawler":
                return (
                    <Card>
                        <div className="p-6">
                            <h2 className="text-lg font-semibold mb-4">
                                URL Crawler
                            </h2>
                            <URLCrawler onCrawl={handleCrawlURL} />
                        </div>
                    </Card>
                );
            case "categories":
                return (
                    <Card>
                        <div className="p-6">
                            <CategoryManagement />
                        </div>
                    </Card>
                );
            default:
                return null;
        }
    };

    return (
        <Layout>
            <AdminSidebar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                stats={stats}
            />
            <div className="ml-64 min-h-screen bg-gray-50">
                <div className="container mx-auto px-4 py-8">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
                    </div>

                    {/* Stats Overview - Only show on all tools page */}
                    {activeTab === "all" && (
                        <StatsOverview
                            pendingCount={stats.pending}
                            approvedCount={stats.approved}
                            rejectedCount={stats.rejected}
                            errorCount={stats.error}
                        />
                    )}

                    {/* Main Content */}
                    <div className="space-y-4">{renderContent()}</div>

                    {/* Tool Details Modal */}
                    {showToolDetails && (
                        <ToolDetailsModal
                            tool={selectedTool}
                            isEditing={isEditing}
                            onClose={() => setShowToolDetails(false)}
                            onEditClick={handleEditClick}
                            onSaveEdit={handleSaveEdit}
                            onCancelEdit={handleCancelEdit}
                            onApproveTool={handleApproveTool}
                            onRejectTool={handleRejectTool}
                            selectedCategoryId={selectedCategoryId}
                            selectedSubCategoryId={selectedSubCategoryId}
                            onCategoryChange={handleCategoryChange}
                            onSubCategoryChange={handleSubCategoryChange}
                            categories={categories}
                        />
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default AdminDashboard;
